import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiRequest, ApiError } from "../api/client";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import AnalysisSubnav from "../components/dashboard/AnalysisSubnav";
import { MONTH_NAMES } from "../constants/dates";
import { formatCurrency } from "../utils/currency";
import { CreditCard } from "../types/dashboard";
import { Bar, CartesianGrid, ComposedChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { IconChevronRight } from "../components/common/WireframeIcons";
import { FloatingToast } from "../components/common/FloatingToast";

type MaybeAll<T extends number> = T | "all";

interface CardTransaction {
  id: number;
  description: string | null;
  amount: number;
  transaction_date: string;
  value_date: string | null;
  card: {
    id: number;
    name: string;
    currency: string;
  };
  category: {
    id: number;
    name: string;
    kind: "income" | "expense" | "transfer";
    color: string | null;
  } | null;
}

interface Filters {
  year: number;
  cardId: MaybeAll<number>;
}

function extractErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

export default function CardTransactionsPage() {
  const { token, logout } = useAuth();
  const currentYear = new Date().getFullYear();

  const [cards, setCards] = React.useState<CreditCard[]>([]);
  const [transactions, setTransactions] = React.useState<CardTransaction[]>([]);
  const [categoryTableView, setCategoryTableView] = React.useState<"total" | number>("total");

  const [filters, setFilters] = React.useState<Filters>({
    year: currentYear,
    cardId: "all",
  });

  const [loadingBase, setLoadingBase] = React.useState(true);
  const [loadingTransactions, setLoadingTransactions] = React.useState(true);
  const [globalError, setGlobalError] = React.useState<string | null>(null);

  const notifyError = React.useCallback((message: string) => {
    setGlobalError(message);
  }, []);

  const handleRequest = React.useCallback(
    async <T,>(promise: Promise<T>): Promise<T> => {
      try {
        return await promise;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          logout();
        }
        throw error;
      }
    },
    [logout]
  );

  const loadBaseData = React.useCallback(async () => {
    if (!token) return;
    setLoadingBase(true);
    setGlobalError(null);
    try {
      const cardsData = await handleRequest(apiRequest<CreditCard[]>("/cards", { token }));
      setCards(cardsData);
    } catch (error) {
      notifyError(extractErrorMessage(error, "No pudimos cargar las tarjetas"));
    } finally {
      setLoadingBase(false);
    }
  }, [token, handleRequest, notifyError]);

  const loadTransactions = React.useCallback(async () => {
    if (!token) return;
    setLoadingTransactions(true);
    const params = new URLSearchParams();
    params.set("year", String(filters.year));
    if (filters.cardId !== "all") params.set("cardId", String(filters.cardId));

    const endpoint = `/card-transactions${params.toString() ? `?${params.toString()}` : ""}`;
    try {
      const rows = await handleRequest(apiRequest<CardTransaction[]>(endpoint, { token }));
      setTransactions(rows);
    } catch (error) {
      notifyError(extractErrorMessage(error, "No pudimos obtener los movimientos de tarjeta"));
    } finally {
      setLoadingTransactions(false);
    }
  }, [token, filters, handleRequest, notifyError]);

  React.useEffect(() => {
    if (!token) return;
    loadBaseData();
  }, [token, loadBaseData]);

  React.useEffect(() => {
    if (!token) return;
    loadTransactions();
  }, [token, loadTransactions]);

  const years = React.useMemo(
    () => Array.from({ length: 6 }, (_, index) => currentYear - index),
    [currentYear]
  );

  const yearlyCardExpenses = React.useMemo(() => {
    return transactions.filter(tx => {
      const date = new Date(tx.transaction_date);
      if (date.getFullYear() !== filters.year) return false;
      if (filters.cardId !== "all" && tx.card.id !== Number(filters.cardId)) return false;
      return tx.amount < 0;
    });
  }, [transactions, filters.year, filters.cardId]);

  const monthlyExpensesData = React.useMemo(
    () =>
      MONTH_NAMES.map((month, index) => {
        const total = yearlyCardExpenses
          .filter(tx => new Date(tx.transaction_date).getMonth() === index)
          .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
        return { month, monthShort: month.slice(0, 3), expenses: total };
      }),
    [yearlyCardExpenses]
  );

  const cardTotalsSummary = React.useMemo(() => {
    const totalExpenses = yearlyCardExpenses.reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
    return {
      totalExpenses,
      avgMonthly: totalExpenses / 12,
    };
  }, [yearlyCardExpenses]);

  const categoryBreakdown = React.useMemo(() => {
    const map = new Map<number, { id: number; name: string; color: string | null; total: number; monthly: number[] }>();
    yearlyCardExpenses.forEach(tx => {
      if (!tx.category?.id) return;
      const key = tx.category.id;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: tx.category.name,
          color: tx.category.color ?? "#94a3b8",
          total: 0,
          monthly: Array.from({ length: 12 }, () => 0),
        });
      }
      const entry = map.get(key)!;
      const amount = Math.abs(tx.amount);
      const month = new Date(tx.transaction_date).getMonth();
      entry.total += amount;
      entry.monthly[month] += amount;
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [yearlyCardExpenses]);

  const pieData = React.useMemo(
    () =>
      categoryBreakdown.slice(0, 10).map(row => ({
        name: row.name,
        value: row.total,
        color: row.color ?? "#94a3b8",
      })),
    [categoryBreakdown]
  );

  const categoryRowsForPeriod = React.useMemo(
    () =>
      categoryBreakdown
        .map(row => ({
          row,
          amount: categoryTableView === "total" ? row.total : row.monthly[categoryTableView],
        }))
        .filter(item => item.amount > 0),
    [categoryBreakdown, categoryTableView]
  );

  const selectedPeriodTotal = React.useMemo(
    () => categoryRowsForPeriod.reduce((sum, item) => sum + item.amount, 0),
    [categoryRowsForPeriod]
  );

  const handlePreviousPeriod = React.useCallback(() => {
    if (categoryTableView === "total") {
      setCategoryTableView(11);
      return;
    }
    if (categoryTableView === 0) {
      setCategoryTableView("total");
      return;
    }
    setCategoryTableView(categoryTableView - 1);
  }, [categoryTableView]);

  const handleNextPeriod = React.useCallback(() => {
    if (categoryTableView === "total") {
      setCategoryTableView(0);
      return;
    }
    if (categoryTableView === 11) {
      setCategoryTableView("total");
      return;
    }
    setCategoryTableView(categoryTableView + 1);
  }, [categoryTableView]);

  return (
    <DashboardLayout active="insights">
      <div className="analysis-page-header">
        <div className="analysis-page-header-inner">
          <div className="analysis-page-heading">
            <h1>Análisis y resultados</h1>
            <p>Consulta tus ingresos, gastos y tendencias financieras</p>
          </div>
          <label className="analysis-year-filter">
            <span>Año</span>
            <select
              className="input"
              value={filters.year}
              onChange={event => setFilters(prev => ({ ...prev, year: Number(event.target.value) }))}
            >
              {years.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="analysis-page-subnav">
        <div className="analysis-page-subnav-inner">
          <AnalysisSubnav mode="cards" />
        </div>
      </div>

      <div className="dashboard-page-content analysis-page-content grid" style={{ gap: "1.5rem" }}>
        <FloatingToast message={globalError} tone="error" onClear={() => setGlobalError(null)} />

        {loadingBase ? (
          <div className="panel">
            <div className="loader">
              <div className="spinner" />
              Cargando tarjetas...
            </div>
          </div>
        ) : (
          <>
            <section className="panel">
              <div className="panel-header">
                <div>
                  <h3 className="panel-title">Desglose de gastos en tarjetas</h3>
                  <p className="panel-subtitle">Análisis detallado de gastos con tarjetas de crédito</p>
                </div>
              </div>
              <div className="analysis-card-selector">
                <button
                  type="button"
                  className={`analysis-card-chip${filters.cardId === "all" ? " is-active" : ""}`}
                  onClick={() => setFilters(prev => ({ ...prev, cardId: "all" }))}
                >
                  Todas
                </button>
                {cards.map(card => (
                  <button
                    type="button"
                    key={card.id}
                    className={`analysis-card-chip${filters.cardId === card.id ? " is-active" : ""}`}
                    onClick={() => setFilters(prev => ({ ...prev, cardId: card.id }))}
                  >
                    {card.name}
                  </button>
                ))}
              </div>
            </section>

            <section className="panel">
              <div className="panel-header">
                <h3 className="panel-title">Resumen de gasto</h3>
              </div>
              <div className="cards-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
                <article className="card">
                  <strong>Total gastado en {filters.year}</strong>
                  <span className="stat-value negative">{formatCurrency(-Math.abs(cardTotalsSummary.totalExpenses))}</span>
                </article>
                <article className="card">
                  <strong>Promedio mensual</strong>
                  <span className="stat-value">{formatCurrency(-Math.abs(cardTotalsSummary.avgMonthly))}</span>
                </article>
              </div>
            </section>

            <section className="panel">
              <div className="panel-header">
                <h3 className="panel-title">Evolución mensual de gastos</h3>
              </div>
              <div style={{ width: "100%", height: "300px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={monthlyExpensesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthShort" />
                    <YAxis />
                    <Tooltip formatter={(value: number) => formatCurrency(-Math.abs(value))} />
                    <Bar dataKey="expenses" fill="#ef4444" name="Gastos" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="panel">
              <div className="panel-header">
                <h3 className="panel-title">Gastos por categoría</h3>
              </div>
              {loadingTransactions ? (
                <div className="loader">
                  <div className="spinner" />
                  Cargando movimientos...
                </div>
              ) : categoryBreakdown.length ? (
                <>
                  <div style={{ width: "100%", height: "300px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={96}
                          label={({ percent }) => `${((percent ?? 0) * 100).toFixed(0)}%`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`pie-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => formatCurrency(-Math.abs(value))} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: "0.4rem",
                      marginBottom: "0.4rem",
                    }}
                  >
                    <button type="button" className="icon-button" onClick={handlePreviousPeriod} aria-label="Periodo anterior">
                      <span style={{ display: "inline-flex", transform: "rotate(180deg)" }}>
                        <IconChevronRight className="w-4 h-4" />
                      </span>
                    </button>
                    <span className="chip" style={{ minWidth: "110px", textAlign: "center" }}>
                      {categoryTableView === "total" ? "Total anual" : MONTH_NAMES[categoryTableView]}
                    </span>
                    <button type="button" className="icon-button" onClick={handleNextPeriod} aria-label="Periodo siguiente">
                      <IconChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <table className="table">
                    <thead>
                      <tr>
                        <th>Categoría</th>
                        <th style={{ textAlign: "right" }}>Gasto</th>
                        <th style={{ textAlign: "right" }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryRowsForPeriod
                        .map(({ row, amount }) => {
                          const percentage = selectedPeriodTotal > 0 ? (amount / selectedPeriodTotal) * 100 : 0;
                          return { row, amount, percentage };
                        })
                        .map(({ row, amount, percentage }) => (
                          <tr key={`category-row-${row.id}`}>
                            <td>
                              <span className="category-color-label">
                                <span className="category-color-dot" style={{ backgroundColor: row.color ?? "#94a3b8" }} />
                                <span>{row.name}</span>
                              </span>
                            </td>
                            <td style={{ textAlign: "right" }}>{formatCurrency(-Math.abs(amount))}</td>
                            <td style={{ textAlign: "right" }}>{percentage.toFixed(1)}%</td>
                          </tr>
                        ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th style={{ textAlign: "right" }}>{formatCurrency(-Math.abs(selectedPeriodTotal))}</th>
                        <th style={{ textAlign: "right" }}>100%</th>
                      </tr>
                    </tfoot>
                  </table>
                </>
              ) : (
                <div className="empty-state">No hay movimientos de tarjeta para los filtros seleccionados.</div>
              )}
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
